import React, { forwardRef } from "react";
import { Button, Card, InputNumber, Input, DatePicker, Select } from "antd";
import { Calendar, Plus, Trash2 } from "lucide-react";
import dayjs from "dayjs";
import { ChequeInput } from "../../Page/pos/common/CashSale/useCashSaleCheckout";

interface ChequePaymentOptionProps {
  cheques: ChequeInput[];
  onChange: (cheques: ChequeInput[]) => void;
  bankAccounts: any[];
}

export const ChequePaymentOption = forwardRef<any, ChequePaymentOptionProps>(
  ({ cheques, onChange, bankAccounts = [] }, ref) => {
    const handleAddCheque = () => {
      onChange([
        ...cheques,
        {
          id: String(Math.random()),
          cheque_no: "",
          bank_name: "",
          cheque_date: dayjs().format("YYYY-MM-DD"),
          amount: 0,
          bank_account_id: "",
        },
      ]);
    };

    const handleRemoveCheque = (id: string) => {
      onChange(cheques.filter((ch) => ch.id !== id));
    };

    const handleUpdateCheque = (
      id: string,
      field: keyof ChequeInput,
      value: any,
    ) => {
      onChange(
        cheques.map((ch) => (ch.id === id ? { ...ch, [field]: value } : ch)),
      );
    };

    return (
      <div className="border border-gray-150 rounded-xl p-3 bg-white hover:border-indigo-300 transition-all flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <span className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
            <Calendar className="w-4 h-4 text-blue-500" /> Cheques (
            {cheques.length})
          </span>
          <Button
            type="dashed"
            size="small"
            onClick={handleAddCheque}
            className="flex items-center gap-1 text-[11px]"
            icon={<Plus className="w-3 h-3" />}
          >
            Add Cheque
          </Button>
        </div>

        {cheques.length === 0 ? (
          <p className="text-[10px] text-gray-400 italic my-1">
            No cheques added yet.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {cheques.map((ch, idx) => (
              <Card
                key={ch.id}
                size="small"
                className="border-gray-200 bg-gray-50/20"
                styles={{ body: { padding: "10px" } }}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                    Cheque #{idx + 1}
                  </span>
                  <Button
                    type="text"
                    danger
                    size="small"
                    onClick={() => handleRemoveCheque(ch.id)}
                    icon={<Trash2 className="w-3.5 h-3.5" />}
                    className="p-0 h-6 w-6 flex items-center justify-center hover:bg-red-50 rounded"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="flex flex-col gap-1">
                    <label className="text-[9px] font-semibold text-gray-400">
                      Cheque Amount *
                    </label>
                    <InputNumber
                      ref={idx === 0 ? ref : undefined}
                      value={ch.amount}
                      onChange={(v) =>
                        handleUpdateCheque(ch.id, "amount", v || 0)
                      }
                      min={0}
                      size="small"
                      className="w-full"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[9px] font-semibold text-gray-400">
                      Cheque Number *
                    </label>
                    <Input
                      value={ch.cheque_no}
                      onChange={(e) =>
                        handleUpdateCheque(ch.id, "cheque_no", e.target.value)
                      }
                      size="small"
                      placeholder="Cheque No"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[9px] font-semibold text-gray-400">
                      Bank Name *
                    </label>
                    <Input
                      value={ch.bank_name}
                      onChange={(e) =>
                        handleUpdateCheque(ch.id, "bank_name", e.target.value)
                      }
                      size="small"
                      placeholder="e.g. HNB, BOC"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[9px] font-semibold text-gray-400">
                      Cheque Date *
                    </label>
                    <DatePicker
                      value={ch.cheque_date ? dayjs(ch.cheque_date) : null}
                      onChange={(date) =>
                        handleUpdateCheque(
                          ch.id,
                          "cheque_date",
                          date ? date.format("YYYY-MM-DD") : "",
                        )
                      }
                      size="small"
                      className="w-full"
                    />
                  </div>
                  <div className="flex flex-col gap-1 col-span-2">
                    <label className="text-[9px] font-semibold text-gray-400">
                      Target Account (Optional)
                    </label>
                    <Select
                      value={ch.bank_account_id || undefined}
                      onChange={(v) =>
                        handleUpdateCheque(ch.id, "bank_account_id", v)
                      }
                      size="small"
                      className="w-full"
                      placeholder="Select bank..."
                      options={bankAccounts.map((ba: any) => ({
                        value: ba.id,
                        label: `${ba.bank_name} - ${ba.account_number} (${ba.account_name})`,
                      }))}
                    />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  },
);
